import React, { useCallback, useContext, useMemo, useRef } from 'react'
import { Auth } from '@grammarly/identity-ui'

import { useApiService, useBrowserService, useExperimentService, useUserService } from '../../lib/hooks'
import { extensionChangedUser, extensionWaitUntilReady, trackAccountCreated, trackLoginSuccess } from './utils'
import { navHelper } from '../../lib/services/navigation'
import { goToEditor } from '../../lib/utils/navigate'
import { User } from '../../lib/services/user'
import { logError, LogErrorMetrics } from '../../lib/tracking/felog'
import { transformToObject } from '../../lib/utils/error'
import { PageContext } from '../../contexts/page-context'
import { UnifiedAuthTracker } from './tracking'
import { authConfig, getUnifiedAuthLogger } from './config'

export interface ExternalProps
  extends Pick<
    Auth.Props.Auth,
    | 'disabledSocialProviders'
    | 'hideTitle'
    | 'hideTitleLink'
    | 'isIgnoreSignInSSO'
    | 'initialEmail'
    | 'initialError'
    | 'customEmailValidation'
    | 'onFormRenderError'
    | 'onBeforeSocialRedirect'
    | 'onAssignEmailVerificationTreatment'
    | 'initialEmailDisabled'
  > {
  supplementaryFlowType?: Auth.Props.SupplementaryFlowType
  // Link to the supplementary flow. Only used in case of supplementaryFlowType = SupplementaryFlowType.Link
  supplementaryFlowLink?: string

  // Callbacks on Auth success, attempt, and reject
  onRedirectAfterAuthSuccess?: (authContext: Auth.Context, user: Auth.User, defaultRedirect: () => void) => unknown
  onAfterAuthAttempt?: (authContext: Auth.Context) => void
  onAfterAuthReject?: (authContext: Auth.Context, err: Auth.Error.AuthCommonError) => void
}

export const withDefaultAuthProps = <P extends Auth.Props.Base>(
  AuthComponent: React.ComponentType<P>
): React.ComponentType<ExternalProps & Partial<P>> => {
  return (props: ExternalProps & Partial<P>) => {
    const apiService = useApiService()
    const userService = useUserService()
    const { isMarketingEmailHoldBackEnabled, isMarketingEmailControlEnabled, isGDPR } = useExperimentService()
    const browserService = useBrowserService()
    const { pageId, slug } = useContext(PageContext)
    const claimedDomainRequest = useRef<Promise<boolean> | null>(null)

    const unifiedAuthTracker: Auth.Tracking.Auth = useMemo(
      () => new UnifiedAuthTracker(pageId, slug, isGDPR),
      [pageId, slug, isGDPR]
    )

    const {
      onRedirectAfterAuthSuccess, // Extract it to avoid passing in ...restAuthProps
      onAfterAuthReject, // Extract it to avoid passing in ...restAuthProps
      onAfterAuthAttempt, // Extract it to avoid passing in ...restAuthProps
      ...restAuthProps
    } = props

    const getCustomFieldsFromGates = useCallback(() => {
      const marketingEmailHoldBack = isMarketingEmailHoldBackEnabled
        ? { marketingEmailHoldBack: true }
        : isMarketingEmailControlEnabled
          ? { marketingEmailHoldBack: false }
          : null

      return marketingEmailHoldBack ? marketingEmailHoldBack : {}
    }, [isMarketingEmailControlEnabled, isMarketingEmailHoldBackEnabled])

    const getCurrentUser = useCallback((): Auth.User | undefined => {
      const user = userService.currentUser
      if (!user.isNone) {
        return user.coreUser
      }
      return undefined
    }, [userService.currentUser])

    const updateUserInUserService = useCallback(
      (_: Auth.Context, user: Auth.User) => {
        userService.setUser(User.create(user))
      },
      [userService]
    )

    const updateUserInExtension = useCallback(async (authContext: Auth.Context) => {
      if (Auth.isAuthSSOContext(authContext) || Auth.isAuthSocialContext(authContext)) {
        // we need to delay because extension is not immediately initialized on the page
        await extensionWaitUntilReady()
      }

      // Send event to extension to update the current user
      extensionChangedUser()
    }, [])

    const trackAuthenticationEvent = useCallback((authContext: Auth.Context, authUser: Auth.User) => {
      const user = User.create(authUser)
      if (authContext.action === 'SIGNUP') {
        trackAccountCreated(user)
      } else if (authContext.action === 'SIGNIN') {
        trackLoginSuccess(user)
      }
    }, [])

    const checkClaimedEmail = useCallback(
      async (email: string) => {
        try {
          return await apiService.institution.checkClaimed(email)
        } catch (e) {
          return false
        }
      },
      [apiService.institution]
    )

    const defaultRedirectAfterAuthSuccess = useCallback(
      (authContext: Auth.Context) => {
        if (browserService.isMobileOrTablet) {
          return navHelper.navigateByRedirectService(navHelper.currentQueryParams).catch((err: Error) =>
            logError(
              `Error navigating by redirect after successful authentication on mobile, authAction = ${authContext.action}`,
              LogErrorMetrics.UnifiedAuth,
              {
                error: transformToObject(err),
              }
            ).then(
              () => navHelper.navigateFunnelPath('/m/desktop-only', navHelper.currentQueryParams),
              () => navHelper.navigateFunnelPath('/m/desktop-only', navHelper.currentQueryParams)
            )
          )
        }

        if (authContext.action === 'SIGNUP') {
          return navHelper.navigateFunnelPath('/personalize', navHelper.currentQueryParams)
        }

        return navHelper.navigateByRedirectService(navHelper.currentQueryParams).catch((err: Error) =>
          logError(
            `Error navigating by redirect after successful authentication, authAction = ${authContext.action}`,
            LogErrorMetrics.UnifiedAuth,
            {
              error: transformToObject(err),
            }
          ).then(
            () => goToEditor(),
            () => goToEditor()
          )
        )
      },
      [browserService.isMobileOrTablet]
    )

    const onAuthSuccess = useCallback(
      async (authContext: Auth.Context, user: Auth.User) => {
        updateUserInUserService(authContext, user)
        await updateUserInExtension(authContext)
        trackAuthenticationEvent(authContext, user)

        if (authContext.action === 'SIGNUP') {
          const isClaimed = await claimedDomainRequest.current
          if (isClaimed) {
            return navHelper.navigateFunnelPath('/business/join')
          }

          if (Auth.isAuthSSOContext(authContext)) {
            return navHelper.navigateFunnelPath('/business/member-welcome')
          }
        }

        if (onRedirectAfterAuthSuccess) {
          try {
            return await onRedirectAfterAuthSuccess(authContext, user, () =>
              defaultRedirectAfterAuthSuccess(authContext)
            )
          } catch (err) {
            logError(
              `Error using onRedirectAfterAuthSuccess after successful authentication, authAction = ${authContext.action}`,
              LogErrorMetrics.UnifiedAuth,
              { error: transformToObject(err as Error) }
            )
          }
        }

        return defaultRedirectAfterAuthSuccess(authContext)
      },
      [
        defaultRedirectAfterAuthSuccess,
        onRedirectAfterAuthSuccess,
        trackAuthenticationEvent,
        updateUserInExtension,
        updateUserInUserService,
      ]
    )

    const onAuthAttempt = useCallback(
      (authContext: Auth.Context) => {
        // explicitly check that action is 'SIGNUP' as sign in through sign up is possible
        if (Auth.isAuthEmailContext(authContext) && authContext.action === 'SIGNUP') {
          claimedDomainRequest.current = checkClaimedEmail(authContext.email)
        }
        props.onAfterAuthAttempt?.(authContext)
      },
      [checkClaimedEmail, props]
    )

    const onAuthReject = useCallback(
      (authContext: Auth.Context, err: Auth.Error.AuthCommonError) => {
        props.onAfterAuthReject?.(authContext, err)
      },
      [props]
    )

    return (
      // @ts-expect-error Due to lack of proper typings. We should fix it
      <AuthComponent
        api={apiService.auth}
        tracker={unifiedAuthTracker}
        logger={getUnifiedAuthLogger()}
        config={authConfig}
        isGDPR={isGDPR}
        getAssignedCustomFields={getCustomFieldsFromGates}
        getCurrentUser={getCurrentUser}
        onAuthSuccess={onAuthSuccess}
        onAuthAttempt={onAuthAttempt}
        onAuthReject={onAuthReject}
        supplementaryFlowType={Auth.Props.SupplementaryFlowType.BuiltIn}
        {...restAuthProps}
      />
    )
  }
}
